import { colorPrimitives } from './color-primitives'
import {
  semanticColors,
  colorScale,
  spacing,
  radius,
  typography,
} from './tokens'
import type { SemanticColors, Spacing, Radius, TypographyToken } from './tokens'

import type { ColorPrimitive } from './color-primitives'
export type { ColorPrimitive } from './color-primitives'
export { colorPrimitives } from './color-primitives'
export type UiColor =
  | 'primary'
  | 'primaryHover'
  | 'primaryActive'
  | 'primaryFocus'
  | 'secondary'
  | 'secondaryHover'
  | 'secondaryActive'
  | 'secondaryFocus'
  | 'neutral'
  | 'neutralHover'
  | 'neutralActive'
  | 'neutralFocus'

export type TShirtSize = 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
export type Scale =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 10
  | 12
  | 14
  | 16
  | 20
  | 24
  | 32
export type Size = TShirtSize | Scale

export type FontSize =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'default'
  | 'large'
  | 'xl'
  | 'body'
  | 'bodyLarge'
  | 'bodySmall'
  | 'button'
  | 'buttonText'
  | 'caption'

export type FontWeights = 'light' | 'regular' | 'medium' | 'semiBold' | 'bold'

export type BorderRadius =
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'xxlarge'
  | 'pill'

type BoxShadow = 'small' | 'medium' | 'large'

type ZIndex = 'select' | 'navigation' | 'languagePicker' | 'modal'

export type Color = ColorPrimitive | UiColor

export interface Theme {
  /** @deprecated Migrate to `semantic` colors */
  colors: Record<Color, string>
  /** @deprecated Migrate to `spacing` */
  sizes: Record<Size, string>
  /** @deprecated Migrate to `typographyTokens` */
  fontSizes: Record<FontSize, string>
  fontWeights: Record<FontWeights, string>
  /** @deprecated Migrate to `radii` */
  borderRadii: Record<BorderRadius, string>
  boxShadows: Record<BoxShadow, string>
  zIndex: Record<ZIndex, string>
  transitionTime: string

  // New token layers — use these in new components
  semantic: SemanticColors
  scale: typeof colorScale
  spacing: Spacing
  radii: Radius
  typographyTokens: Record<string, TypographyToken>
}

export const theme: Theme = {
  colors: {
    ...colorPrimitives,
    primary: colorPrimitives.primary400,
    primaryHover: colorPrimitives.primary600,
    primaryActive: colorPrimitives.primary600,
    primaryFocus: colorPrimitives.primary600,
    secondary: colorPrimitives.secondary400,
    secondaryHover: colorPrimitives.secondary600,
    secondaryActive: colorPrimitives.secondary600,
    secondaryFocus: colorPrimitives.secondary600,
    neutral: colorPrimitives.neutral400,
    neutralHover: colorPrimitives.neutral600,
    neutralActive: colorPrimitives.neutral600,
    neutralFocus: colorPrimitives.neutral600,
  },
  sizes: {
    xxs: '0.25rem',
    xs: '0.5rem',
    s: '1rem',
    m: '1.5rem',
    l: '2rem',
    xl: '2.5rem',
    xxl: '4rem',
    0: '0rem',
    1: '0.25rem',
    2: '0.5rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    10: '2.5rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    32: '8rem',
  },
  fontSizes: {
    h1: '2.5rem',
    h2: '1.5rem',
    h3: '1.25rem',
    h4: '1.125rem',
    h5: '1rem',
    h6: '0.875rem',
    default: '0.875rem',
    large: '1rem',
    body: '1rem',
    bodyLarge: '1.125rem',
    bodySmall: '0.875rem',
    button: '0.875rem',
    buttonText: '0.875rem',
    caption: '0.75rem',
    xl: '2.125rem',
  },
  fontWeights: {
    light: '300',
    regular: '400',
    medium: '500',
    semiBold: '600',
    bold: '700',
  },
  borderRadii: {
    small: '0.125rem',
    medium: '0.25rem',
    large: '0.5rem',
    xlarge: '1rem',
    xxlarge: '62.5rem',
    pill: '9999px',
  },
  boxShadows: {
    small: '0 0.125rem 1rem rgba(33, 37, 53, 0.08)',
    medium: '0 0.5rem 2rem rgba(33, 37, 53, 0.16)',
    large: '0 2.5rem 4.5rem rgba(33, 37, 53, 0.16)',
  },
  zIndex: {
    select: '100',
    navigation: '1200',
    languagePicker: '1201',
    modal: '1210',
  },
  transitionTime: '200ms',

  // New token layers
  semantic: semanticColors,
  scale: colorScale,
  spacing,
  radii: radius,
  typographyTokens: typography,
}
